import React from 'react';
import { Location, WorkLocation } from '../types';
import { isWithinAnyWorkRange, formatDistance } from '../utils';

interface LocationStatusProps {
  currentLocation: Location | null;
  workLocation: Location;
  allWorkLocations: WorkLocation[];
  nearestLocation: WorkLocation | null;
  locationError: string;
  canCheckIn: boolean;
}

const LocationStatus: React.FC<LocationStatusProps> = ({
  currentLocation,
  allWorkLocations,
  nearestLocation,
  locationError,
  canCheckIn
}) => {
  const getDistanceInfo = () => {
    if (!currentLocation || allWorkLocations.length === 0) return null;
    
    const result = isWithinAnyWorkRange(currentLocation, allWorkLocations, 1);
    
    return {
      distance: result.distance,
      isWithinRange: result.isWithinRange,
      nearestLocation: result.nearestLocation,
      status: result.isWithinRange ? 'Herhangi bir iş yerine yakın' : 'Hiçbir iş yerine yeterince yakın değil'
    };
  };

  const distanceInfo = getDistanceInfo();

  return (
    <div className="space-y-4">
      {/* Current Location */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-2">Mevcut Konum</h4>
        {currentLocation ? (
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-600">
              Enlem: {currentLocation.latitude.toFixed(6)}
            </p>
            <p className="text-sm text-gray-600">
              Boylam: {currentLocation.longitude.toFixed(6)}
            </p>
            {currentLocation.address && (
              <p className="text-sm text-gray-800 font-medium mt-2">
                📍 {currentLocation.address}
              </p>
            )}
          </div>
        ) : locationError ? (
          <div className="bg-red-50 p-3 rounded-md">
            <p className="text-sm text-red-600">{locationError}</p>
          </div>
        ) : (
          <div className="bg-yellow-50 p-3 rounded-md">
            <p className="text-sm text-yellow-600">Konum alınıyor...</p>
          </div>
        )}
      </div>

      {/* Nearest Work Location */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          En Yakın İş Yeri Konumu
          {allWorkLocations.length > 1 && (
            <span className="text-xs text-gray-500 ml-1">
              ({allWorkLocations.length} konum mevcut)
            </span>
          )}
        </h4>
        {nearestLocation ? (
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <p className="text-sm text-gray-600">
              Enlem: {nearestLocation.latitude.toFixed(6)}
            </p>
            <p className="text-sm text-gray-600">
              Boylam: {nearestLocation.longitude.toFixed(6)}
            </p>
            <p className="text-sm text-blue-800 font-medium mt-2">
              🏢 {nearestLocation.name}
            </p>
            {nearestLocation.address && (
              <p className="text-sm text-gray-700 mt-1">
                📍 {nearestLocation.address}
              </p>
            )}
          </div>
        ) : allWorkLocations.length > 0 ? (
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-600">
              Konum bilgisi alınırken en yakın iş yeri hesaplanıyor...
            </p>
          </div>
        ) : (
          <div className="bg-yellow-50 p-3 rounded-md">
            <p className="text-sm text-yellow-600">
              Henüz hiçbir iş yeri konumu tanımlanmamış
            </p>
          </div>
        )}
      </div>

      {/* Distance Status */}
      {distanceInfo && distanceInfo.nearestLocation && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            En Yakın Konuma Mesafe
          </h4>
          <div className={`p-3 rounded-md ${
            distanceInfo.isWithinRange ? 'bg-green-50' : 'bg-red-50'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {formatDistance(distanceInfo.distance)}
              </span>
              <span className={`text-sm font-medium ${
                distanceInfo.isWithinRange ? 'text-green-800' : 'text-red-800'
              }`}>
                {distanceInfo.status}
              </span>
            </div>
            <p className="text-xs text-gray-600">
              📍 {distanceInfo.nearestLocation.name}
            </p>
          </div>
        </div>
      )}

      {/* Check-in Status */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-2">Giriş Durumu</h4>
        <div className={`p-3 rounded-md ${
          canCheckIn ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className="flex items-center">
            <div className={`w-2 h-2 rounded-full mr-2 ${
              canCheckIn ? 'bg-green-400' : 'bg-red-400'
            }`}></div>
            <span className={`text-sm font-medium ${
              canCheckIn ? 'text-green-800' : 'text-red-800'
            }`}>
              {canCheckIn 
                ? `Giriş yapmaya hazır${nearestLocation ? ` (${nearestLocation.name})` : ''}` 
                : allWorkLocations.length > 0 
                  ? 'Giriş yapılamaz - hiçbir iş yerine yeterince yakın değil'
                  : 'Giriş yapılamaz - iş yeri konumu tanımlanmamış'
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationStatus; 